// src/components/Overeni.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Reservation } from '../types';
import { verifyReservation } from '../services/api';

const Overeni: React.FC = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'transfer' | 'cash' | null>(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Scroll na začátek stránky
    window.scrollTo(0, 0);

    // Načtení reservation a paymentMethod ze state
    const state = location.state as { reservation: Reservation; paymentMethod: 'transfer' | 'cash' | null } | null;
    if (state && state.reservation && state.paymentMethod) {
      setReservation(state.reservation);
      setPaymentMethod(state.paymentMethod);
    } else {
      // Pokud state není k dispozici, pokusíme se načíst z localStorage
      const data = localStorage.getItem('reservationData');
      if (data) {
        const parsed = JSON.parse(data) as Reservation;
        setReservation(parsed);
        setPaymentMethod(parsed.paymentMethod || null);
      } else {
        void navigate('/rezervace');
      }
    }
  }, [navigate, location]);

  const handleVerify = async () => {
    if (!reservation || !paymentMethod) {
      setError('Chybí údaje o rezervaci nebo způsobu platby.');
      return;
    }

    if (typeof reservation.id !== 'number') {
      setError('Chybí ID rezervace.');
      return;
    }

    try {
      await verifyReservation(reservation.id, code);

      const updatedReservation: Reservation = {
        ...reservation,
        isVerified: true,
        paymentMethod: paymentMethod ?? reservation.paymentMethod,
      };
      localStorage.setItem('reservationData', JSON.stringify(updatedReservation));

      if (paymentMethod === 'transfer') {
        void navigate('/transfer-payment', { state: { reservation: updatedReservation } });
      } else if (paymentMethod === 'cash') {
        void navigate('/cash-payment', { state: { reservation: updatedReservation } });
      } else {
        setError('Není zvolen platný způsob platby.');
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(t('verify.error') + ': ' + err.message);
      } else {
        setError(t('verify.error') + ': Neznámá chyba.');
      }
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 p-6 bg-white rounded shadow">
      <h2 className="text-2xl font-bold mb-4">{t('verify.title')}</h2>
      <p className="mb-4 text-gray-600">
        {t('verify.instructions')}
      </p>
      <input
        type="text"
        value={code}
        onChange={e => setCode(e.target.value)}
        placeholder={t('verify.codePlaceholder')}
        className="w-full px-4 py-2 border rounded mb-4"
      />
      {error && <p className="text-red-500 mb-2">{error}</p>}
      <button
        onClick={() => void handleVerify()}
        className="w-full bg-[#4B2E2E] text-white px-6 py-2 rounded hover:bg-[#3a1e1e]"
      >
        {t('verify.submit')}
      </button>
    </div>
  );
};

export default Overeni;
